import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  MdDashboard,
  MdPeople,
  MdAssignment,
  MdPerson,
  MdExitToApp,
  MdChevronRight,
  MdApartment,
  MdSwapHoriz,
  MdClose
} from 'react-icons/md';
import { FiArrowRight } from 'react-icons/fi';

// Paleta de cores do Branding
const colors = {
  primary: '#5AB9D3',       // Azul claro principal
  primaryLight: '#6AC9E3',  // Azul claro mais claro
  primaryDark: '#4AA5C0',   // Azul claro mais escuro
  secondary: '#101A3A',     // Azul escuro principal
  secondaryLight: '#1A2747', // Azul escuro mais claro
  white: '#FFFFFF',         // Branco puro
  textLight: '#E2E8F0',     // Branco acinzentado claro
  accent: '#38A169',        // Verde para destaques
  error: '#E53E3E',         // Vermelho para erros
  success: '#38A169'        // Verde para sucesso
};

// Animação de entrada
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// Efeito de brilho
const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(90, 185, 211, 0.3); }
  50% { box-shadow: 0 0 20px rgba(90, 185, 211, 0.6); }
  100% { box-shadow: 0 0 5px rgba(90, 185, 211, 0.3); }
`;

const SidebarContainer = styled.div`
  width: 240px;
  min-height: 100vh;
  height: 100%;
  background: linear-gradient(160deg, ${colors.secondary} 0%, ${colors.secondaryLight} 100%);
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  overflow-x: hidden;
  animation: ${slideIn} 0.5s ease-out;
  box-shadow: 5px 0 25px rgba(0, 0, 0, 0.4);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at 80% 20%,
      rgba(90, 185, 211, 0.15) 0%,
      transparent 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.primary};
    border-radius: 3px;
  }

  @media (max-width: 992px) {
    transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    width: 280px;
    box-shadow: ${({ $isOpen }) => $isOpen ? '5px 0 30px rgba(0, 0, 0, 0.5)' : 'none'};
    z-index: 1100;
  }

  @media (max-width: 576px) {
    width: 100%;
    max-width: 320px;
  }
`;

const MobileHeader = styled.div`
  display: none;
  padding: 15px 20px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(90, 185, 211, 0.3);
  margin-bottom: 15px;

  @media (max-width: 992px) {
    display: flex;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.white};
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }
`;

const UserPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.primary}, transparent);
    opacity: 0.3;
  }

  @media (max-width: 576px) {
    padding: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  color: ${colors.white};
  font-size: 32px;
  font-weight: bold;
  box-shadow: 0 4px 15px rgba(90, 185, 211, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(90, 185, 211, 0.6);
  }

  @media (max-width: 576px) {
    width: 70px;
    height: 70px;
    font-size: 28px;
  }
`;

const UserName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.white};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  @media (max-width: 576px) {
    font-size: 16px;
  }
`;

const UserEmail = styled.p`
  margin: 5px 0 0;
  font-size: 14px;
  color: ${colors.textLight};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  @media (max-width: 576px) {
    font-size: 13px;
  }
`;

const UserPlan = styled.div`
  margin-top: 10px;
  padding: 5px 12px;
  background: rgba(90, 185, 211, 0.2);
  color: ${colors.primary};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border: 1px solid ${colors.primary};
  animation: ${glow} 3s infinite;

  @media (max-width: 576px) {
    font-size: 11px;
    padding: 4px 10px;
  }
`;

const MenuContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 0 15px;
  margin-top: 10px;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 15px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.$isActive ? colors.white : colors.textLight};
  background: ${props => props.$isActive ? `linear-gradient(90deg, ${colors.primary} 0%, rgba(90, 185, 211, 0.3) 100%)` : 'transparent'};
  position: relative;
  overflow: hidden;

  &:hover {
    background: rgba(90, 185, 211, 0.15);
    color: ${colors.white};
    transform: translateX(5px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: ${colors.primary};
    transform: ${props => props.$isActive ? 'scaleY(1)' : 'scaleY(0)'};
    transform-origin: bottom;
    transition: transform 0.3s ease;
  }

  svg {
    margin-right: 12px;
    font-size: 20px;
    color: ${props => props.$isActive ? colors.white : colors.primary};
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  &:hover svg {
    color: ${colors.white};
    transform: scale(1.1);
  }

  @media (max-width: 576px) {
    padding: 10px 12px;
  }
`;

const MenuText = styled.span`
  font-size: 15px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const MenuBadge = styled.span`
  background: ${colors.primary};
  color: ${colors.secondary};
  font-size: 11px;
  font-weight: bold;
  padding: 3px 8px;
  border-radius: 10px;
  margin-left: 10px;
  flex-shrink: 0;

  @media (max-width: 576px) {
    font-size: 10px;
    padding: 2px 6px;
  }
`;

const SubMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 20px;
  padding-left: 15px;
  border-left: 1px dashed rgba(90, 185, 211, 0.3);
  overflow: hidden;
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  transition: max-height 0.4s ease;
`;

const SubMenuItem = styled.div`
  padding: 10px 15px;
  margin: 3px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.$isActive ? colors.white : colors.textLight};
  background: ${props => props.$isActive ? `rgba(90, 185, 211, 0.3)` : 'transparent'};
  font-size: 14px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: rgba(90, 185, 211, 0.15);
    color: ${colors.white};
  }

  &::before {
    content: '•';
    color: ${colors.primary};
    margin-right: 10px;
    font-size: 18px;
  }

  @media (max-width: 576px) {
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(90, 185, 211, 0.3), transparent);
  margin: 15px;
`;

const MenuSectionTitle = styled.div`
  padding: 10px 15px;
  margin: 10px 0 5px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.primary};
  font-weight: 600;
  display: flex;
  align-items: center;
  white-space: nowrap;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(90, 185, 211, 0.3), transparent);
    margin: 0 10px;
  }

  @media (max-width: 576px) {
    font-size: 11px;
  }
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(90, 185, 211, 0.3);
  color: ${colors.white};
  margin: 20px 15px 0;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(5px);
  width: calc(100% - 30px);
  box-sizing: border-box;

  &:hover {
    background: ${colors.primary};
    border-color: ${colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(90, 185, 211, 0.4);
  }

  svg {
    margin-right: 10px;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 0;
    border-radius: 100%;
    transform: scale(1, 1) translate(-50%, -50%);
    transform-origin: 50% 50%;
  }

  &:focus:not(:active)::after {
    animation: ripple 0.6s ease-out;
  }

  @keyframes ripple {
    0% {
      transform: scale(0, 0);
      opacity: 0.5;
    }
    100% {
      transform: scale(20, 20);
      opacity: 0;
    }
  }

  @media (max-width: 576px) {
    font-size: 13px;
    padding: 10px;
  }
`;

const CollapseIcon = styled.span`
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(0)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    realocation: false,
    clients: false,
    properties: false
  });

  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const userPlan = user?.plan || 'Free';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const toggleMenu = (menu) => {
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleItemClick = () => {
    if (window.innerWidth <= 992) {
      toggleSidebar();
    }
  };

  if (!user) return null;

  // Itens do menu de Realocação
  const realocationItems = [
    { to: '/realocation/register', label: 'Cadastrar Realocação', icon: <MdSwapHoriz /> },
    { to: '/realocation/management', label: 'Gerenciamento', icon: <MdAssignment /> }
  ];

  // Itens do menu de Clientes
  const clientsItems = [
    { to: '/clients/register', label: 'Cadastrar Cliente', icon: <MdPeople /> },
    { to: '/clients/management', label: 'Gerenciar Clientes', icon: <MdAssignment /> },
  ];

  // Itens do menu de Imóveis
  const propertiesItems = [
    { to: '/real-estate/register', label: 'Cadastrar Imóvel', icon: <MdApartment /> },
    { to: '/real-estate/management', label: 'Gerenciar Imóveis', icon: <MdAssignment /> },
  ];

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    return names.length > 1
      ? `${names[0][0]}${names[names.length - 1][0]}`
      : names[0][0];
  };

  return (
    <SidebarContainer $isOpen={isOpen}>
      <MobileHeader>
        <h3>Menu</h3>
        <CloseButton onClick={toggleSidebar}>
          <MdClose />
        </CloseButton>
      </MobileHeader>

      <UserPanel>
        <UserAvatar>
          {user?.photoUrl ? (
            <img src={user.photoUrl} alt="User Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
          ) : (
            getInitials(user?.name)
          )}
        </UserAvatar>
        <UserName>{user?.name || 'Usuário'}</UserName>
        <UserEmail>{user?.email || 'usuario@email.com'}</UserEmail>
        <UserPlan>{userPlan === 'Pro' ? 'Plano Pro' : 'Plano Free'}</UserPlan>
      </UserPanel>

      <MenuContainer>
        <MenuItem
          $isActive={location.pathname === '/dashboard'}
          onClick={() => {
            navigate('/dashboard');
            handleItemClick();
          }}
        >
          <MdDashboard size={20} />
          <MenuText $isActive={location.pathname === '/dashboard'}>Dashboard</MenuText>
          {userPlan === 'Pro' && <MenuBadge>PRO</MenuBadge>}
        </MenuItem>

        <MenuDivider />

        {/* Seção de Imóveis */}
        <MenuSectionTitle>Imóveis</MenuSectionTitle>

        <MenuItem
          $isActive={location.pathname.includes('/real-estate')}
          onClick={() => toggleMenu('properties')}
        >
          <MdApartment size={20} />
          <MenuText $isActive={location.pathname.includes('/real-estate')}>
            Imóveis
          </MenuText>
          <CollapseIcon $isOpen={openMenus.properties}>
            <MdChevronRight size={18} />
          </CollapseIcon>
        </MenuItem>

        <SubMenuContainer $isOpen={openMenus.properties}>
          {propertiesItems.map((item) => (
            <SubMenuItem
              key={item.to}
              $isActive={location.pathname === item.to}
              onClick={() => {
                navigate(item.to);
                handleItemClick();
              }}
            >
              {item.label}
            </SubMenuItem>
          ))}
        </SubMenuContainer>

        {/* Seção Clientes */}
        <MenuSectionTitle>Clientes</MenuSectionTitle>

        <MenuItem
          $isActive={location.pathname.includes('/clients')}
          onClick={() => toggleMenu('clients')}
        >
          <MdPeople size={20} />
          <MenuText $isActive={location.pathname.includes('/clients')}>
            Clientes
          </MenuText>
          <CollapseIcon $isOpen={openMenus.clients}>
            <MdChevronRight size={18} />
          </CollapseIcon>
        </MenuItem>

        <SubMenuContainer $isOpen={openMenus.clients}>
          {clientsItems.map((item) => (
            <SubMenuItem
              key={item.to}
              $isActive={location.pathname === item.to}
              onClick={() => {
                navigate(item.to);
                handleItemClick();
              }}
            >
              {item.label}
            </SubMenuItem>
          ))}
        </SubMenuContainer>

        {/* Seção Realocação */}
        <MenuSectionTitle>Realocação</MenuSectionTitle>

        <MenuItem
          $isActive={location.pathname.includes('/realocation')}
          onClick={() => toggleMenu('realocation')}
        >
          <MdSwapHoriz size={20} />
          <MenuText $isActive={location.pathname.includes('/realocation')}>
            Realocação
          </MenuText>
          <CollapseIcon $isOpen={openMenus.realocation}>
            <MdChevronRight size={18} />
          </CollapseIcon>
        </MenuItem>

        <SubMenuContainer $isOpen={openMenus.realocation}>
          {realocationItems.map((item) => (
            <SubMenuItem
              key={item.to}
              $isActive={location.pathname === item.to}
              onClick={() => {
                navigate(item.to);
                handleItemClick();
              }}
            >
              {item.label}
            </SubMenuItem>
          ))}
        </SubMenuContainer>

        {/* {userPlan === 'Pro' && (
          <>
            <MenuDivider />
            <MenuItem
              $isActive={location.pathname === '/tasks'}
              onClick={() => {
                navigate('/tasks');
                handleItemClick();
              }}
            >
              <MdAssignment size={20} />
              <MenuText $isActive={location.pathname === '/tasks'}>Tarefas</MenuText>
              <MenuBadge>PRO</MenuBadge>
            </MenuItem>
          </>
        )} */}

        <MenuDivider />

        <MenuItem
          $isActive={location.pathname === '/profile'}
          onClick={() => {
            navigate('/profile');
            handleItemClick();
          }}
        >
          <MdPerson size={20} />
          <MenuText $isActive={location.pathname === '/profile'}>Perfil</MenuText>
        </MenuItem>

        {/* <MenuItem
          $isActive={location.pathname === '/faq'}
          onClick={() => {
            navigate('/faq');
            handleItemClick();
          }}
        >
          <MdHelp size={20} />
          <MenuText $isActive={location.pathname === '/faq'}>Ajuda & FAQ</MenuText>
        </MenuItem> */}
        <MenuItem
          $isActive={location.pathname === '/payment'}
          onClick={() => {
            navigate('/payment');
            handleItemClick();
          }}
        >
          <FiArrowRight size={20} />
          <MenuText $isActive={location.pathname === '/payment'}>Ver Planos</MenuText>
        </MenuItem>
      </MenuContainer>

      <LogoutButton onClick={handleLogout}>
        <MdExitToApp size={18} />
        <span>Sair</span>
      </LogoutButton>
    </SidebarContainer>
  );
};

export default Sidebar;